/**
 * Admin Console
 * User management interface for administrators
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, User as ApiUser, CreateUserResponse, Viewer, EventSchedule, Affiliation, ViewerListParams, UserListParams, BulkNotificationCreate } from '../lib/api-client';
import { ROUTES } from '../lib/constants';
import type { ScheduleStatus } from '../lib/api-types';
import SubtitleSettingsPanel from '../components/SubtitleSettingsPanel';
import EKYCAdminPanel from '../components/EKYCAdminPanel';
import BannerManagementPanel from '../components/BannerManagementPanel';
import BackupManagementPanel from '../components/BackupManagementPanel';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { AnalyticsLogger } from '../lib/analytics';
import ReportsManagementPanel from '../components/ReportsManagementPanel';
import ModerationConsole from '../components/moderation/ModerationConsole';
import { resolveMediaUrl } from '../lib/media-url';

type TabType = 'accounts' | 'affiliations' | 'schedules' | 'settings' | 'notifications' | 'moderation' | 'reports' | 'stt' | 'ekyc' | 'banners' | 'backups';
type AccountSubTab = 'admins' | 'managers' | 'streamers' | 'viewers';

export default function Admin() {
  const { user: currentUser, logout } = useAuth();
  const analytics = AnalyticsLogger.getInstance();
  const [activeTab, setActiveTab] = useState<TabType>('schedules');
  // MANAGERは管理者/マネージャータブにアクセスできないので、初期タブを調整
  const [accountSubTab, setAccountSubTab] = useState<AccountSubTab>('streamers');

  // タブ切り替えハンドラー（ログ付き）
  const handleTabChange = useCallback((newTab: TabType) => {
    analytics.trackTabChange(newTab, activeTab);
    setActiveTab(newTab);
  }, [activeTab, analytics]);

  // サブタブ切り替えハンドラー（ログ付き）
  const handleAccountSubTabChange = useCallback((newSubTab: AccountSubTab) => {
    analytics.trackTabChange(`accounts.${newSubTab}`, `accounts.${accountSubTab}`);
    setAccountSubTab(newSubTab);
    setShowCreateUserForm(false);
  }, [accountSubTab, analytics]);
  const [isTabInitialized, setIsTabInitialized] = useState(false);
  const isAdmin = currentUser?.role === 'ADMIN';

  // Users state
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newRole, setNewRole] = useState<'ADMIN' | 'MANAGER' | 'STREAMER'>('STREAMER');
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Viewers state
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [isLoadingViewers, setIsLoadingViewers] = useState(true);
  // Viewer pagination state
  const [viewerPage, setViewerPage] = useState(1);
  const [viewerLimit, setViewerLimit] = useState(20);
  const [viewerTotal, setViewerTotal] = useState(0);
  const [viewerTotalPages, setViewerTotalPages] = useState(1);
  // Viewer filter state
  const [viewerFilters, setViewerFilters] = useState<Record<string, string>>({
    username: '',
    created_from: '',
    created_to: '',
  });
  // Viewer selection state
  const [selectedViewerIds, setSelectedViewerIds] = useState<Set<number>>(new Set());
  // Bulk notification modal state
  const [showBulkNotificationModal, setShowBulkNotificationModal] = useState(false);
  const [bulkNotificationTitle, setBulkNotificationTitle] = useState('');
  const [bulkNotificationMessage, setBulkNotificationMessage] = useState('');
  const [bulkNotificationLinkUrl, setBulkNotificationLinkUrl] = useState('');
  const [isSendingBulkNotification, setIsSendingBulkNotification] = useState(false);

  // Streamer pagination state
  const [streamerList, setStreamerList] = useState<ApiUser[]>([]);
  const [isLoadingStreamers, setIsLoadingStreamers] = useState(true);
  const [streamerPage, setStreamerPage] = useState(1);
  const [streamerLimit, setStreamerLimit] = useState(20);
  const [streamerTotal, setStreamerTotal] = useState(0);
  const [streamerTotalPages, setStreamerTotalPages] = useState(1);
  // Streamer filter state
  const [streamerFilters, setStreamerFilters] = useState<Record<string, string>>({
    username: '',
    affiliation_id: '',
    created_from: '',
    created_to: '',
  });

  // Schedules state
  const [schedules, setSchedules] = useState<EventSchedule[]>([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);
  const [showCreateScheduleForm, setShowCreateScheduleForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<EventSchedule | null>(null);
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const [originalScheduleDuration, setOriginalScheduleDuration] = useState<number>(0); // 元の差分（ミリ秒）
  const [newScheduleStreamerId, setNewScheduleStreamerId] = useState<number>(0);
  const [newScheduleStart, setNewScheduleStart] = useState('');
  const [newScheduleEnd, setNewScheduleEnd] = useState('');
  const [newScheduleStatus, setNewScheduleStatus] = useState<ScheduleStatus>('scheduled');
  const [streamers, setStreamers] = useState<ApiUser[]>([]);

  // Site settings state
  const [scheduleNoticePrefix, setScheduleNoticePrefix] = useState('');
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSchedulePrefix, setIsSavingSchedulePrefix] = useState(false);

  // Notifications state
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationLinkUrl, setNotificationLinkUrl] = useState('');
  const [notificationImageUrl, setNotificationImageUrl] = useState('');
  const [notificationImageFile, setNotificationImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageUploadWarning, setImageUploadWarning] = useState('');
  const [showImageConfirmModal, setShowImageConfirmModal] = useState(false);
  const [pendingImageData, setPendingImageData] = useState<{ url: string; warning: string } | null>(null);
  const [notificationRecipientType, setNotificationRecipientType] = useState<'all' | 'individual'>('all');
  const [notificationRecipientId, setNotificationRecipientId] = useState<number | undefined>(undefined);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [showNotificationSuccessModal, setShowNotificationSuccessModal] = useState(false);

  // Affiliations state (Admin only)
  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [isLoadingAffiliations, setIsLoadingAffiliations] = useState(true);
  const [showCreateAffiliationForm, setShowCreateAffiliationForm] = useState(false);
  const [editingAffiliation, setEditingAffiliation] = useState<Affiliation | null>(null);
  const [newAffiliationName, setNewAffiliationName] = useState('');
  const [newAffiliationDescription, setNewAffiliationDescription] = useState('');
  const [newUserAffiliationId, setNewUserAffiliationId] = useState<number | undefined>(undefined);

  // Edit user state
  const [editingUser, setEditingUser] = useState<ApiUser | null>(null);
  const [editUserUsername, setEditUserUsername] = useState('');
  const [editUserAffiliationId, setEditUserAffiliationId] = useState<number | undefined>(undefined);
  const [editUserNewPassword, setEditUserNewPassword] = useState<string | null>(null);

  // Shared state
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editScheduleError, setEditScheduleError] = useState('');

  useEffect(() => {
    loadUsers();
    loadViewers();
    loadStreamers();
    loadSchedules();
    loadSettings();
    if (currentUser?.role === 'ADMIN') {
      loadAffiliations();
    }
  }, [currentUser]);

  // Update accountSubTab when currentUser loads (for proper ADMIN vs MANAGER initial tab)
  useEffect(() => {
    if (currentUser && !isTabInitialized) {
      const userIsAdmin = currentUser.role === 'ADMIN';
      setAccountSubTab(userIsAdmin ? 'admins' : 'streamers');
      setIsTabInitialized(true);
    }
  }, [currentUser, isTabInitialized]);

  // Extract streamers from users for schedule dropdown
  useEffect(() => {
    setStreamers(users.filter(u => u.role === 'STREAMER'));
  }, [users]);

  const loadUsers = async () => {
    setIsLoadingUsers(true);
    setError('');
    try {
      const response = await apiClient.listUsers({ limit: 100 }); // Get all users for admin/manager tabs (max 100)
      setUsers(response.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ユーザーの読み込みに失敗しました');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadViewers = async (page: number = viewerPage, limit: number = viewerLimit, filters: Record<string, string> = viewerFilters) => {
    setIsLoadingViewers(true);
    setError('');
    try {
      const params: ViewerListParams = {
        page,
        limit,
      };
      if (filters.username) params.username = filters.username;
      if (filters.created_from) params.created_from = filters.created_from;
      if (filters.created_to) params.created_to = filters.created_to;

      const response = await apiClient.listViewers(params);
      setViewers(response.viewers);
      setViewerTotal(response.total);
      setViewerTotalPages(response.total_pages);
      setViewerPage(response.page);
      // Clear selection when data changes
      setSelectedViewerIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : '視聴者の読み込みに失敗しました');
    } finally {
      setIsLoadingViewers(false);
    }
  };

  const loadStreamers = async (page: number = streamerPage, limit: number = streamerLimit, filters: Record<string, string> = streamerFilters) => {
    setIsLoadingStreamers(true);
    setError('');
    try {
      const params: UserListParams = {
        page,
        limit,
        role: 'STREAMER',
      };
      if (filters.username) params.username = filters.username;
      if (filters.affiliation_id) params.affiliation_id = Number(filters.affiliation_id);
      if (filters.created_from) params.created_from = filters.created_from;
      if (filters.created_to) params.created_to = filters.created_to;

      const response = await apiClient.listUsers(params);
      setStreamerList(response.users);
      setStreamerTotal(response.total);
      setStreamerTotalPages(response.total_pages);
      setStreamerPage(response.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : '配信者の読み込みに失敗しました');
    } finally {
      setIsLoadingStreamers(false);
    }
  };

  const loadAffiliations = async () => {
    setIsLoadingAffiliations(true);
    setError('');
    try {
      const affiliationList = await apiClient.listAffiliations();
      setAffiliations(affiliationList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'グループの読み込みに失敗しました');
    } finally {
      setIsLoadingAffiliations(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // マネージャーと配信者はグループ選択必須
    if ((newRole === 'MANAGER' || newRole === 'STREAMER') && !newUserAffiliationId) {
      setError('マネージャーと配信者には所属グループを選択してください');
      return;
    }

    try {
      const result: CreateUserResponse = await apiClient.createUser({
        username: newUsername,
        role: newRole,
        affiliation_id: newUserAffiliationId,
      });
      analytics.trackFormSubmit('create_user', true);
      analytics.trackButtonClick('create_user_submit', `ユーザー作成: ${newRole}`, 'console');
      setCreatedPassword(result.password);
      setPasswordCopied(false);
      setSuccessMessage(`ユーザーが作成されました！ユーザー名: ${result.username}`);
      setNewUsername('');
      setNewRole('STREAMER');
      setNewUserAffiliationId(undefined);
      setShowCreateUserForm(false);
      await loadUsers();
    } catch (err) {
      analytics.trackFormSubmit('create_user', false);
      setError(err instanceof Error ? err.message : 'ユーザーの作成に失敗しました');
    }
  };

  const handleDeleteUser = async (userId: any, username: any) => {
    if (!confirm(`ユーザー「${username}」を削除してもよろしいですか？`)) {
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      await apiClient.deleteUser(userId);
      analytics.trackButtonClick('delete_user', `ユーザー削除: ${username}`, 'console');
      setSuccessMessage(`ユーザー「${username}」を削除しました`);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ユーザーの削除に失敗しました');
    }
  };

  const handleDeleteViewer = async (viewerId: number, username: string) => {
    if (!confirm(`視聴者「${username}」を削除してもよろしいですか？`)) {
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      await apiClient.deleteViewer(viewerId);
      analytics.trackButtonClick('delete_viewer', `視聴者削除: ${username}`, 'console');
      setSuccessMessage(`視聴者「${username}」を削除しました`);
      await loadViewers();
    } catch (err) {
      setError(err instanceof Error ? err.message : '視聴者の削除に失敗しました');
    }
  };

  // Viewer pagination handlers
  const handleViewerPageChange = (page: number) => {
    setViewerPage(page);
    loadViewers(page, viewerLimit, viewerFilters);
  };

  const handleViewerLimitChange = (limit: number) => {
    setViewerLimit(limit);
    setViewerPage(1);
    loadViewers(1, limit, viewerFilters);
  };

  // Viewer filter handlers
  const handleViewerFilterChange = (filters: Record<string, string>) => {
    setViewerFilters(filters);
  };

  const handleViewerSearch = () => {
    setViewerPage(1);
    loadViewers(1, viewerLimit, viewerFilters);
  };

  const handleViewerFilterReset = () => {
    const resetFilters = { username: '', created_from: '', created_to: '' };
    setViewerFilters(resetFilters);
    setViewerPage(1);
    loadViewers(1, viewerLimit, resetFilters);
  };

  // Bulk notification handler (viewers only)
  const handleSendBulkNotification = async () => {
    if (selectedViewerIds.size === 0) return;

    if (!bulkNotificationTitle.trim() || !bulkNotificationMessage.trim()) {
      setError('タイトルと本文は必須です');
      return;
    }

    setIsSendingBulkNotification(true);
    setError('');
    setSuccessMessage('');

    try {
      const data: BulkNotificationCreate = {
        recipient_ids: Array.from(selectedViewerIds),
        recipient_type: 'viewers',
        title: bulkNotificationTitle,
        message: bulkNotificationMessage,
        link_url: bulkNotificationLinkUrl || undefined,
      };

      const result = await apiClient.sendBulkNotification(data);
      analytics.trackButtonClick('bulk_notification_send', `一括通知送信: ${result.sent_count}件`, 'console');
      setSuccessMessage(`${result.sent_count}件の通知を送信しました（失敗: ${result.failed_count}件）`);

      // Reset state
      setShowBulkNotificationModal(false);
      setBulkNotificationTitle('');
      setBulkNotificationMessage('');
      setBulkNotificationLinkUrl('');
      setSelectedViewerIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : '通知の送信に失敗しました');
    } finally {
      setIsSendingBulkNotification(false);
    }
  };

  // Streamer pagination handlers
  const handleStreamerPageChange = (page: number) => {
    setStreamerPage(page);
    loadStreamers(page, streamerLimit, streamerFilters);
  };

  const handleStreamerLimitChange = (limit: number) => {
    setStreamerLimit(limit);
    setStreamerPage(1);
    loadStreamers(1, limit, streamerFilters);
  };

  // Streamer filter handlers
  const handleStreamerFilterChange = (filters: Record<string, string>) => {
    setStreamerFilters(filters);
  };

  const handleStreamerSearch = () => {
    setStreamerPage(1);
    loadStreamers(1, streamerLimit, streamerFilters);
  };

  const handleStreamerFilterReset = () => {
    const resetFilters = { username: '', affiliation_id: '', created_from: '', created_to: '' };
    setStreamerFilters(resetFilters);
    setStreamerPage(1);
    loadStreamers(1, streamerLimit, resetFilters);
  };

  // User edit functions
  const startEditUser = (user: ApiUser) => {
    setEditingUser(user);
    setEditUserUsername(user.username);
    setEditUserAffiliationId(user.affiliation_id || undefined);
    setEditUserNewPassword(null);
    setShowCreateUserForm(false);
  };

  const cancelEditUser = () => {
    setEditingUser(null);
    setEditUserUsername('');
    setEditUserAffiliationId(undefined);
    setEditUserNewPassword(null);
  };

  const handleUpdateUserUsername = async () => {
    if (!editingUser) return;
    if (!editUserUsername.trim()) {
      setError('ユーザー名を入力してください');
      return;
    }
    if (editUserUsername === editingUser.username) {
      setError('ユーザー名が変更されていません');
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      await apiClient.updateUser(editingUser.id, { username: editUserUsername });
      setSuccessMessage(`ユーザー名を「${editUserUsername}」に更新しました`);
      await loadUsers();
      // Update editingUser with new username
      const response = await apiClient.listUsers({ limit: 100 });
      const updatedUser = response.users.find(u => u.id === editingUser.id);
      if (updatedUser) {
        setEditingUser(updatedUser);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ユーザー名の更新に失敗しました');
    }
  };

  const handleResetUserPassword = async () => {
    if (!editingUser) return;

    setError('');
    setSuccessMessage('');

    // Generate random password (16 chars)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let newPassword = '';
    for (let i = 0; i < 16; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    try {
      await apiClient.updateUser(editingUser.id, { password: newPassword });
      setEditUserNewPassword(newPassword);
      setSuccessMessage(`${editingUser.username} のパスワードをリセットしました`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'パスワードのリセットに失敗しました');
    }
  };

  const handleUpdateUserAffiliation = async () => {
    if (!editingUser) return;

    setError('');
    setSuccessMessage('');

    try {
      await apiClient.updateUser(editingUser.id, { affiliation_id: editUserAffiliationId || undefined });
      setSuccessMessage(`${editingUser.username} の所属グループを更新しました`);
      await loadUsers();
      // Update editingUser with new affiliation
      const response = await apiClient.listUsers({ limit: 100 });
      const updatedUser = response.users.find(u => u.id === editingUser.id);
      if (updatedUser) {
        setEditingUser(updatedUser);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '所属グループの更新に失敗しました');
    }
  };

  // Affiliation functions (Admin only)
  const handleCreateAffiliation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      await apiClient.createAffiliation({
        name: newAffiliationName,
        description: newAffiliationDescription || undefined,
      });
      analytics.trackFormSubmit('create_affiliation', true);
      analytics.trackButtonClick('create_affiliation_submit', `グループ作成: ${newAffiliationName}`, 'console');
      setSuccessMessage(`グループ「${newAffiliationName}」を作成しました`);
      setNewAffiliationName('');
      setNewAffiliationDescription('');
      setShowCreateAffiliationForm(false);
      await loadAffiliations();
    } catch (err) {
      analytics.trackFormSubmit('create_affiliation', false);
      setError(err instanceof Error ? err.message : 'グループの作成に失敗しました');
    }
  };

  const handleUpdateAffiliation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAffiliation) return;

    setError('');
    setSuccessMessage('');

    try {
      await apiClient.updateAffiliation(editingAffiliation.id, {
        name: newAffiliationName || undefined,
        description: newAffiliationDescription,
      });
      analytics.trackFormSubmit('update_affiliation', true);
      analytics.trackButtonClick('update_affiliation_submit', `グループ更新: ${newAffiliationName || editingAffiliation.name}`, 'console');
      setSuccessMessage(`グループ「${newAffiliationName || editingAffiliation.name}」を更新しました`);
      setNewAffiliationName('');
      setNewAffiliationDescription('');
      setEditingAffiliation(null);
      await loadAffiliations();
    } catch (err) {
      analytics.trackFormSubmit('update_affiliation', false);
      setError(err instanceof Error ? err.message : 'グループの更新に失敗しました');
    }
  };

  const handleDeleteAffiliation = async (affiliationId: number, affiliationName: string) => {
    if (!confirm(`グループ「${affiliationName}」を削除してもよろしいですか？`)) {
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      await apiClient.deleteAffiliation(affiliationId);
      analytics.trackButtonClick('delete_affiliation', `グループ削除: ${affiliationName}`, 'console');
      setSuccessMessage(`グループ「${affiliationName}」を削除しました`);
      await loadAffiliations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'グループの削除に失敗しました');
    }
  };

  const startEditAffiliation = (affiliation: Affiliation) => {
    setEditingAffiliation(affiliation);
    setNewAffiliationName(affiliation.name);
    setNewAffiliationDescription(affiliation.description || '');
    setShowCreateAffiliationForm(false);
  };

  const resetAffiliationForm = () => {
    setNewAffiliationName('');
    setNewAffiliationDescription('');
    setEditingAffiliation(null);
    setShowCreateAffiliationForm(false);
  };

  // Schedule functions
  const loadSchedules = async () => {
    setIsLoadingSchedules(true);
    setError('');
    try {
      const response = await apiClient.listEventSchedules();
      setSchedules(response.schedules);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'スケジュールの読み込みに失敗しました');
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  const loadSettings = async () => {
    setIsLoadingSettings(true);
    try {
      // Load schedule notice prefix
      const prefix = await apiClient.getCurrentScheduleNoticePrefix();
      setScheduleNoticePrefix(prefix.value || '');
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveScheduleNoticePrefix = async () => {
    setIsSavingSchedulePrefix(true);
    setError('');
    setSuccessMessage('');

    try {
      await apiClient.updateScheduleNoticePrefix(scheduleNoticePrefix);
      analytics.trackFormSubmit('save_schedule_notice_prefix', true);
      analytics.trackButtonClick('save_settings', 'おしらせ固定文言保存', 'console');
      setSuccessMessage('おしらせ固定文言を更新しました');
    } catch (err) {
      analytics.trackFormSubmit('save_schedule_notice_prefix', false);
      setError(err instanceof Error ? err.message : '設定の保存に失敗しました');
    } finally {
      setIsSavingSchedulePrefix(false);
    }
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setNotificationImageFile(file);
    setIsUploadingImage(true);
    setError('');
    setImageUploadWarning('');

    try {
      const result = await apiClient.uploadNotificationImage(file);

      if (result.warning) {
        // Show confirmation modal when aspect ratio is significantly different
        setPendingImageData({ url: result.image_url, warning: result.warning });
        setShowImageConfirmModal(true);
      } else {
        // No warning, directly use the image
        setNotificationImageUrl(result.image_url);
        setSuccessMessage(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '画像のアップロードに失敗しました');
      setNotificationImageFile(null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleConfirmImage = () => {
    if (pendingImageData) {
      setNotificationImageUrl(pendingImageData.url);
      setImageUploadWarning(pendingImageData.warning);
      setSuccessMessage('画像をアップロードしました（244x165にリサイズ済み）');
    }
    setShowImageConfirmModal(false);
    setPendingImageData(null);
  };

  const handleCancelImage = () => {
    setShowImageConfirmModal(false);
    setPendingImageData(null);
    setNotificationImageFile(null);
  };

  const handleRemoveImage = () => {
    setNotificationImageFile(null);
    setNotificationImageUrl('');
    setImageUploadWarning('');
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSendingNotification(true);
    setError('');
    setSuccessMessage('');

    try {
      await apiClient.createNotification({
        title: notificationTitle,
        message: notificationMessage,
        link_url: notificationLinkUrl || undefined,
        image_url: notificationImageUrl || undefined,
        recipient_type: notificationRecipientType,
        recipient_id: notificationRecipientType === 'individual' ? notificationRecipientId : undefined,
      });
      analytics.trackFormSubmit('send_notification', true);
      analytics.trackButtonClick('send_notification_submit', `通知送信: ${notificationRecipientType}`, 'console');
      setNotificationTitle('');
      setNotificationMessage('');
      setNotificationLinkUrl('');
      setNotificationImageUrl('');
      setNotificationImageFile(null);
      setNotificationRecipientType('all');
      setNotificationRecipientId(undefined);
      setShowNotificationSuccessModal(true);
    } catch (err) {
      analytics.trackFormSubmit('send_notification', false);
      setError(err instanceof Error ? err.message : '通知の送信に失敗しました');
    } finally {
      setIsSendingNotification(false);
    }
  };

  const handleCreateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!newScheduleStreamerId) {
      setError('配信者を選択してください');
      return;
    }

    // バリデーション: 終了日時は開始日時より後でなければならない
    if (newScheduleStart && newScheduleEnd) {
      const startTime = new Date(newScheduleStart).getTime();
      const endTime = new Date(newScheduleEnd).getTime();
      if (endTime <= startTime) {
        setError('終了日時は開始日時より後に設定してください');
        return;
      }
    }

    try {
      await apiClient.createEventSchedule({
        streamer_id: newScheduleStreamerId,
        scheduled_start: new Date(newScheduleStart).toISOString(),
        scheduled_end: new Date(newScheduleEnd).toISOString(),
      });
      analytics.trackFormSubmit('create_schedule', true);
      analytics.trackButtonClick('create_schedule_submit', 'スケジュール作成', 'console');
      setSuccessMessage('スケジュールを作成しました');
      resetScheduleForm();
      setShowCreateScheduleForm(false);
      await loadSchedules();
    } catch (err) {
      analytics.trackFormSubmit('create_schedule', false);
      setError(err instanceof Error ? err.message : 'スケジュールの作成に失敗しました');
    }
  };

  const handleUpdateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSchedule) return;

    setEditScheduleError('');
    setSuccessMessage('');

    // バリデーション: 終了日時は開始日時より後でなければならない
    if (newScheduleStart && newScheduleEnd) {
      const startTime = new Date(newScheduleStart).getTime();
      const endTime = new Date(newScheduleEnd).getTime();
      if (endTime <= startTime) {
        setEditScheduleError('終了日時は開始日時より後に設定してください');
        return;
      }
    }

    try {
      const updateData = {
        scheduled_start: newScheduleStart ? new Date(newScheduleStart).toISOString() : undefined,
        scheduled_end: newScheduleEnd ? new Date(newScheduleEnd).toISOString() : undefined,
        status: newScheduleStatus,
      };
      await apiClient.updateEventSchedule(editingSchedule.id, updateData);
      analytics.trackFormSubmit('update_schedule', true);
      analytics.trackButtonClick('update_schedule_submit', 'スケジュール更新', 'console');
      setSuccessMessage('スケジュールを更新しました');
      closeEditScheduleModal();
      await loadSchedules();
    } catch (err) {
      analytics.trackFormSubmit('update_schedule', false);
      setEditScheduleError(err instanceof Error ? err.message : 'スケジュールの更新に失敗しました');
    }
  };

  const handleDeleteSchedule = async (scheduleId: string, streamerName: string) => {
    if (!confirm(`${streamerName}のスケジュールを削除してもよろしいですか？`)) {
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      await apiClient.deleteEventSchedule(scheduleId);
      analytics.trackButtonClick('delete_schedule', `スケジュール削除: ${streamerName}`, 'console');
      setSuccessMessage('スケジュールを削除しました');
      await loadSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'スケジュールの削除に失敗しました');
    }
  };

  const startEditSchedule = (schedule: EventSchedule) => {
    setEditingSchedule(schedule);
    const startTime = new Date(schedule.scheduled_start).getTime();
    const endTime = new Date(schedule.scheduled_end).getTime();
    setOriginalScheduleDuration(endTime - startTime);
    setNewScheduleStart(formatDateTimeLocal(schedule.scheduled_start));
    setNewScheduleEnd(formatDateTimeLocal(schedule.scheduled_end));
    setNewScheduleStatus(schedule.status as ScheduleStatus);
    setShowCreateScheduleForm(false);
    setShowEditScheduleModal(true);
  };

  const resetScheduleForm = () => {
    setNewScheduleStreamerId(0);
    setNewScheduleStart('');
    setNewScheduleEnd('');
  };

  // 編集時に開始時間を変更したときに終了時間も自動調整する
  const handleEditStartTimeChange = (newStart: string) => {
    setNewScheduleStart(newStart);
    if (newStart && originalScheduleDuration > 0) {
      const newStartTime = new Date(newStart).getTime();
      const newEndTime = new Date(newStartTime + originalScheduleDuration);
      // ローカルタイムゾーンでdatetime-local形式の文字列を生成
      const year = newEndTime.getFullYear();
      const month = String(newEndTime.getMonth() + 1).padStart(2, '0');
      const day = String(newEndTime.getDate()).padStart(2, '0');
      const hours = String(newEndTime.getHours()).padStart(2, '0');
      const minutes = String(newEndTime.getMinutes()).padStart(2, '0');
      setNewScheduleEnd(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  };

  const closeEditScheduleModal = () => {
    setShowEditScheduleModal(false);
    setEditingSchedule(null);
    setEditScheduleError('');
    resetScheduleForm();
  };

  const formatDateTimeLocal = (isoString: string) => {
    const date = new Date(isoString);
    // ローカルタイムゾーンでdatetime-local形式の文字列を生成
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // 15分刻みの時刻オプションを生成
  const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
    const hours = Math.floor(i / 4);
    const minutes = (i % 4) * 15;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  });

  // 日付と時刻を結合してdatetime-local形式にする
  const combineDateAndTime = (date: string, time: string): string => {
    if (!date || !time) return '';
    return `${date}T${time}`;
  };

  // datetime-local形式から日付部分を取得
  const getDatePart = (dateTime: string): string => {
    if (!dateTime) return '';
    return dateTime.split('T')[0] || '';
  };

  // datetime-local形式から時刻部分を取得
  const getTimePart = (dateTime: string): string => {
    if (!dateTime) return '';
    const timePart = dateTime.split('T')[1] || '';
    // 時刻を15分単位に丸める
    if (timePart) {
      const [hours, mins] = timePart.split(':');
      const roundedMins = Math.round(parseInt(mins || '0') / 15) * 15;
      return `${hours}:${String(roundedMins % 60).padStart(2, '0')}`;
    }
    return '';
  };

  const formatDateTime = (isoString: string) => {
    return new Date(isoString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'live':
        return 'bg-red-100 text-red-800';
      case 'ended':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-orange-100 text-orange-800';
      case 'paused':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled': return '予定';
      case 'live': return '配信中';
      case 'ended': return '終了';
      case 'cancelled': return 'キャンセル';
      case 'suspended': return '中断';
      case 'paused': return '一時停止';
      default: return status;
    }
  };

  const handleCopyPassword = async () => {
    if (!createdPassword) return;
    try {
      await navigator.clipboard.writeText(createdPassword);
      setPasswordCopied(true);
      // コピー後、自動的にパスワードを非表示
      setTimeout(() => {
        setCreatedPassword(null);
        setPasswordCopied(false);
      }, 1000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">管理コンソール</h1>
              <p className="text-sm text-gray-600">
                ログイン中: <span className="text-info font-semibold">{currentUser?.username}</span> ({currentUser?.role})
              </p>
            </div>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 transition-colors shadow-md"
            >
              ログアウト
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-500 text-red-600 px-4 py-3 mb-4 font-medium">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-success-50 border border-success text-success-700 px-4 py-3 mb-4 font-medium">
            {successMessage}
          </div>
        )}

        {createdPassword && !passwordCopied && (
          <div className="bg-accent-50 border border-accent text-accent-800 px-4 py-3 mb-4">
            <p className="font-semibold mb-2">⚠️ このパスワードを保存してください - 1度だけ表示されます:</p>
            <div className="flex items-center gap-3">
              <p className="font-mono text-lg flex-1">{createdPassword}</p>
              <button
                onClick={handleCopyPassword}
                className="bg-accent hover:bg-accent-600 text-white font-semibold py-2 px-4 transition-colors flex items-center gap-2 shadow-md"
              >
                📋 コピー
              </button>
            </div>
            <p className="text-sm text-accent-700 mt-2">※ コピーボタンをクリックすると、このメッセージは2度と表示されません</p>
          </div>
        )}

        {passwordCopied && (
          <div className="bg-success-50 border border-success text-success-700 px-4 py-3 mb-4 font-medium">
            ✅ パスワードをクリップボードにコピーしました
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 shadow-lg max-w-lg w-full mx-4 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  アカウント編集: {editingUser.username}
                </h3>
                <button
                  onClick={cancelEditUser}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* User Info */}
                <div className="bg-gray-50 p-3 border border-gray-200">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">ID:</span> {editingUser.id}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">ロール:</span> {editingUser.role}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">現在の所属:</span> {editingUser.affiliation_name || 'default'}
                  </p>
                </div>

                {/* Username Change Section */}
                <div className="border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">ユーザー名変更</h4>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editUserUsername}
                      onChange={(e) => setEditUserUsername(e.target.value)}
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="新しいユーザー名"
                    />
                    <button
                      onClick={handleUpdateUserUsername}
                      disabled={editUserUsername === editingUser.username}
                      className={`font-semibold py-2 px-4 transition-colors ${
                        editUserUsername === editingUser.username
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      更新
                    </button>
                  </div>
                </div>

                {/* Password Reset Section */}
                <div className="border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">パスワードリセット</h4>
                  {editUserNewPassword ? (
                    <div className="bg-yellow-50 border border-yellow-200 p-3">
                      <p className="text-sm text-yellow-800 font-medium mb-2">⚠️ 新しいパスワード（保存してください）:</p>
                      <div className="flex items-center gap-2">
                        <code className="bg-white px-2 py-1 border border-yellow-300 font-mono flex-1 text-sm">
                          {editUserNewPassword}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(editUserNewPassword);
                            setSuccessMessage('パスワードをコピーしました');
                          }}
                          className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 text-sm font-semibold"
                        >
                          コピー
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleResetUserPassword}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 transition-colors"
                    >
                      パスワードをリセット
                    </button>
                  )}
                </div>

                {/* Affiliation Change Section (Admin only, for MANAGER and STREAMER) */}
                {isAdmin && editingUser.role !== 'ADMIN' && (
                  <div className="border border-gray-200 p-4">
                    <h4 className="font-semibold text-gray-800 mb-3">所属グループ変更</h4>
                    <div className="flex gap-2">
                      <select
                        value={editUserAffiliationId || ''}
                        onChange={(e) => setEditUserAffiliationId(e.target.value ? Number(e.target.value) : undefined)}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">デフォルト</option>
                        {affiliations.filter(a => a.name !== 'default').map((affiliation) => (
                          <option key={affiliation.id} value={affiliation.id}>{affiliation.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={handleUpdateUserAffiliation}
                        className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 transition-colors"
                      >
                        更新
                      </button>
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={cancelEditUser}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 transition-colors"
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => handleTabChange('accounts')}
            className={`py-2 px-6 font-semibold transition-all ${
              activeTab === 'accounts'
                ? 'bg-pink-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            アカウント管理
          </button>
          {isAdmin && (
            <button
              onClick={() => handleTabChange('affiliations')}
              className={`py-2 px-6 font-semibold transition-all ${
                activeTab === 'affiliations'
                  ? 'bg-pink-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              グループ管理
            </button>
          )}
          <button
            onClick={() => handleTabChange('schedules')}
            className={`py-2 px-6 font-semibold transition-all ${
              activeTab === 'schedules'
                ? 'bg-pink-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            イベント管理
          </button>
          <button
            onClick={() => handleTabChange('settings')}
            className={`py-2 px-6 font-semibold transition-all ${
              activeTab === 'settings'
                ? 'bg-pink-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            サイト設定
          </button>
          <button
            onClick={() => handleTabChange('notifications')}
            className={`py-2 px-6 font-semibold transition-all ${
              activeTab === 'notifications'
                ? 'bg-pink-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            通知管理
          </button>
          <button
            onClick={() => handleTabChange('moderation')}
            className={`py-2 px-6 font-semibold transition-all ${
              activeTab === 'moderation' ? 'bg-red-700 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            監視・措置
          </button>
          <button
            onClick={() => handleTabChange('reports')}
            className={`py-2 px-6 font-semibold transition-all ${
              activeTab === 'reports' ? 'bg-red-700 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            通報管理
          </button>
          {isAdmin && (
            <button
              onClick={() => handleTabChange('stt')}
              className={`py-2 px-6 font-semibold transition-all ${
                activeTab === 'stt'
                  ? 'bg-pink-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              字幕設定
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => handleTabChange('ekyc')}
              className={`py-2 px-6 font-semibold transition-all ${
                activeTab === 'ekyc'
                  ? 'bg-pink-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              本人確認
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => handleTabChange('banners')}
              className={`py-2 px-6 font-semibold transition-all ${
                activeTab === 'banners'
                  ? 'bg-pink-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              バナー管理
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => handleTabChange('backups')}
              className={`py-2 px-6 font-semibold transition-all ${
                activeTab === 'backups'
                  ? 'bg-pink-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              バックアップ
            </button>
          )}
        </div>


        {/* Accounts Tab - Combined account management */}
        {activeTab === 'accounts' && (
          <div className="bg-white shadow-md p-6 mb-6 border border-gray-200">
            {/* Account Sub-tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200 pb-4">
              {/* 管理者タブはADMINのみ表示 */}
              {isAdmin && (
                <button
                  onClick={() => handleAccountSubTabChange('admins')}
                  className={`py-2 px-4 font-semibold transition-all ${
                    accountSubTab === 'admins'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  管理者 ({users.filter(u => u.role === 'ADMIN').length})
                </button>
              )}
              {/* マネージャータブはADMINのみ表示（MANAGERは自分より上位のロールを管理できない） */}
              {isAdmin && (
                <button
                  onClick={() => handleAccountSubTabChange('managers')}
                  className={`py-2 px-4 font-semibold transition-all ${
                    accountSubTab === 'managers'
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  マネージャー ({users.filter(u => u.role === 'MANAGER').length})
                </button>
              )}
              <button
                onClick={() => handleAccountSubTabChange('streamers')}
                className={`py-2 px-4 font-semibold transition-all ${
                  accountSubTab === 'streamers'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                配信者 ({users.filter(u => u.role === 'STREAMER').length})
              </button>
              <button
                onClick={() => handleAccountSubTabChange('viewers')}
                className={`py-2 px-4 font-semibold transition-all ${
                  accountSubTab === 'viewers'
                    ? 'bg-info text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ユーザー ({viewers.length})
              </button>
            </div>

            {/* Admins Sub-tab */}
            {accountSubTab === 'admins' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">管理者</h2>
                  <button
                    onClick={() => {
                      setShowCreateUserForm(!showCreateUserForm);
                      setNewRole('ADMIN');
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 transition-colors shadow-md"
                  >
                    {showCreateUserForm ? 'キャンセル' : '+ 管理者作成'}
                  </button>
                </div>

                {showCreateUserForm && (
                  <form onSubmit={handleCreateUser} className="mb-6 p-4 bg-red-50 border border-red-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="new-username" className="block text-sm font-medium text-gray-700 mb-2">ユーザー名</label>
                        <input
                          id="new-username"
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          required
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                          placeholder="ユーザー名を入力"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="submit"
                          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 transition-colors"
                        >
                          作成
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {isLoadingUsers ? (
                  <div className="text-center text-gray-600 py-8">読み込み中...</div>
                ) : users.filter(u => u.role === 'ADMIN').length === 0 ? (
                  <div className="text-center text-gray-600 py-8">管理者が見つかりません</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-gray-700 font-semibold">ID</th>
                          <th className="text-left py-3 px-4 text-gray-700 font-semibold">ユーザー名</th>
                          <th className="text-left py-3 px-4 text-gray-700 font-semibold">作成日</th>
                          <th className="text-right py-3 px-4 text-gray-700 font-semibold">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.filter(u => u.role === 'ADMIN').map((user) => (
                          <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-100/50">
                            <td className="py-3 px-4 text-left text-gray-700">{user.id}</td>
                            <td className="py-3 px-4 text-left text-gray-900 font-medium">{user.username}</td>
                            <td className="py-3 px-4 text-left text-gray-600 text-sm">
                              {new Date(user.created_at).toLocaleDateString('ja-JP')}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => startEditUser(user)}
                                  className="text-blue-600 hover:text-blue-800 font-semibold"
                                >
                                  編集
                                </button>
                                {user.id !== currentUser?.id && (
                                  <button
                                    onClick={() => handleDeleteUser(user.id, user.username)}
                                    className="text-red-600 hover:text-red-800 font-semibold"
                                  >
                                    削除
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Managers Sub-tab */}
            {accountSubTab === 'managers' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">マネージャー</h2>
                  <button
                    onClick={() => {
                      setShowCreateUserForm(!showCreateUserForm);
                      setNewRole('MANAGER');
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 transition-colors shadow-md"
                  >
                    {showCreateUserForm ? 'キャンセル' : '+ マネージャー作成'}
                  </button>
                </div>

                {showCreateUserForm && (
                  <form onSubmit={handleCreateUser} className="mb-6 p-4 bg-orange-50 border border-orange-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label htmlFor="new-manager-username" className="block text-sm font-medium text-gray-700 mb-2">ユーザー名</label>
                        <input
                          id="new-manager-username"
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          required
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          placeholder="ユーザー名を入力"
                        />
                      </div>
                      <div>
                        <label htmlFor="new-manager-affiliation" className="block text-sm font-medium text-gray-700 mb-2">所属グループ</label>
                        <select
                          id="new-manager-affiliation"
                          value={newUserAffiliationId || ''}
                          onChange={(e) => setNewUserAffiliationId(e.target.value ? Number(e.target.value) : undefined)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          required
                        >
                          <option value="" disabled>グループを選択してください</option>
                          {affiliations.filter(a => a.name !== 'default').map((affiliation) => (
                            <option key={affiliation.id} value={affiliation.id}>{affiliation.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button
                          type="submit"
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 transition-colors"
                        >
                          作成
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {isLoadingUsers ? (
                  <div className="text-center text-gray-600 py-8">読み込み中...</div>
                ) : users.filter(u => u.role === 'MANAGER').length === 0 ? (
                  <div className="text-center text-gray-600 py-8">マネージャーが見つかりません</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-gray-700 font-semibold">ID</th>
                          <th className="text-left py-3 px-4 text-gray-700 font-semibold">ユーザー名</th>
                          <th className="text-left py-3 px-4 text-gray-700 font-semibold">所属グループ</th>
                          <th className="text-left py-3 px-4 text-gray-700 font-semibold">作成日</th>
                          <th className="text-right py-3 px-4 text-gray-700 font-semibold">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.filter(u => u.role === 'MANAGER').map((user) => (
                          <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-100/50">
                            <td className="py-3 px-4 text-left text-gray-700">{user.id}</td>
                            <td className="py-3 px-4 text-left text-gray-900 font-medium">{user.username}</td>
                            <td className="py-3 px-4 text-left">
                              <span className="px-2 py-1 text-xs bg-teal-100 text-teal-800 rounded">
                                {user.affiliation_name || 'default'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-left text-gray-600 text-sm">
                              {new Date(user.created_at).toLocaleDateString('ja-JP')}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => startEditUser(user)}
                                  className="text-blue-600 hover:text-blue-800 font-semibold"
                                >
                                  編集
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user.id, user.username)}
                                  className="text-red-600 hover:text-red-800 font-semibold"
                                >
                                  削除
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Streamers Sub-tab */}
            {accountSubTab === 'streamers' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">配信者 ({streamerTotal}件)</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowCreateUserForm(!showCreateUserForm);
                        setNewRole('STREAMER');
                      }}
                      className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 transition-colors shadow-md"
                    >
                      {showCreateUserForm ? 'キャンセル' : '+ 配信者作成'}
                    </button>
                  </div>
                </div>

                {/* Streamer Filter Bar */}
                <FilterBar
                  fields={[
                    { name: 'username', label: 'ユーザー名', type: 'text', placeholder: 'ユーザー名で検索' },
                    ...(isAdmin ? [{
                      name: 'affiliation_id',
                      label: '所属グループ',
                      type: 'select' as const,
                      options: affiliations.filter(a => a.name !== 'default').map(a => ({ value: String(a.id), label: a.name })),
                    }] : []),
                    { name: 'created_from', label: '登録日（開始）', type: 'date' },
                    { name: 'created_to', label: '登録日（終了）', type: 'date' },
                  ]}
                  values={streamerFilters}
                  onChange={handleStreamerFilterChange}
                  onSearch={handleStreamerSearch}
                  onReset={handleStreamerFilterReset}
                  isLoading={isLoadingStreamers}
                />

                {showCreateUserForm && (
                  <form onSubmit={handleCreateUser} className="mb-6 p-4 bg-purple-50 border border-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label htmlFor="new-streamer-username" className="block text-sm font-medium text-gray-700 mb-2">ユーザー名</label>
                        <input
                          id="new-streamer-username"
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          required
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="ユーザー名を入力"
                        />
                      </div>
                      {isAdmin && (
                        <div>
                          <label htmlFor="new-streamer-affiliation" className="block text-sm font-medium text-gray-700 mb-2">所属グループ</label>
                          <select
                            id="new-streamer-affiliation"
                            value={newUserAffiliationId || ''}
                            onChange={(e) => setNewUserAffiliationId(e.target.value ? Number(e.target.value) : undefined)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            required
                          >
                            <option value="" disabled>グループを選択してください</option>
                            {affiliations.filter(a => a.name !== 'default').map((affiliation) => (
                              <option key={affiliation.id} value={affiliation.id}>{affiliation.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="flex items-end">
                        <button
                          type="submit"
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 transition-colors"
                        >
                          作成
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {isLoadingStreamers ? (
                  <div className="text-center text-gray-600 py-8">読み込み中...</div>
                ) : streamerList.length === 0 ? (
                  <div className="text-center text-gray-600 py-8">配信者が見つかりません</div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">ID</th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">ユーザー名</th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">所属グループ</th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">本人確認</th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">作成日</th>
                            <th className="text-right py-3 px-4 text-gray-700 font-semibold">操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {streamerList.map((user) => (
                            <tr
                              key={user.id}
                              className="border-b border-gray-200 hover:bg-gray-100/50"
                            >
                              <td className="py-3 px-4 text-left text-gray-700">{user.id}</td>
                              <td className="py-3 px-4 text-left text-gray-900 font-medium">{user.username}</td>
                              <td className="py-3 px-4 text-left">
                                <span className="px-2 py-1 text-xs bg-teal-100 text-teal-800 rounded">
                                  {user.affiliation_name || 'default'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-left">
                                {user.is_verified ? (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded flex items-center gap-1 w-fit">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    確認済み
                                  </span>
                                ) : user.ekyc_status === 'SUBMITTED' || user.ekyc_status === 'API_VERIFYING' ? (
                                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                                    API審査中
                                  </span>
                                ) : user.ekyc_status === 'PENDING_REVIEW' ? (
                                  <button
                                    onClick={() => handleTabChange('ekyc')}
                                    className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200 transition-colors"
                                  >
                                    マネージャ承認待ち →
                                  </button>
                                ) : user.ekyc_status === 'REJECTED' ? (
                                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                                    却下
                                  </span>
                                ) : user.ekyc_status === 'EXPIRED' ? (
                                  <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                                    期限切れ
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                    未申請
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-left text-gray-600 text-sm">
                                {new Date(user.created_at).toLocaleDateString('ja-JP')}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => startEditUser(user)}
                                    className="text-blue-600 hover:text-blue-800 font-semibold"
                                  >
                                    編集
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUser(user.id, user.username)}
                                    className="text-red-600 hover:text-red-800 font-semibold"
                                  >
                                    削除
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                      currentPage={streamerPage}
                      totalPages={streamerTotalPages}
                      totalItems={streamerTotal}
                      itemsPerPage={streamerLimit}
                      onPageChange={handleStreamerPageChange}
                      onItemsPerPageChange={handleStreamerLimitChange}
                    />
                  </>
                )}
              </>
            )}

            {/* Viewers Sub-tab */}
            {accountSubTab === 'viewers' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">ユーザー ({viewerTotal}件)</h2>
                  <div className="flex gap-2">
                    {selectedViewerIds.size > 0 && (
                      <button
                        onClick={() => setShowBulkNotificationModal(true)}
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 transition-colors shadow-md"
                      >
                        選択した{selectedViewerIds.size}件に通知
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter Bar */}
                <FilterBar
                  fields={[
                    { name: 'username', label: 'ユーザー名', type: 'text', placeholder: '部分一致検索' },
                    { name: 'created_from', label: '登録日（開始）', type: 'date' },
                    { name: 'created_to', label: '登録日（終了）', type: 'date' },
                  ]}
                  values={viewerFilters}
                  onChange={handleViewerFilterChange}
                  onSearch={handleViewerSearch}
                  onReset={handleViewerFilterReset}
                  isLoading={isLoadingViewers}
                />

                {isLoadingViewers ? (
                  <div className="text-center text-gray-600 py-8">読み込み中...</div>
                ) : viewers.length === 0 ? (
                  <div className="text-center text-gray-600 py-8">ユーザーが見つかりません</div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 w-12">
                              <input
                                type="checkbox"
                                checked={viewers.length > 0 && viewers.every(v => selectedViewerIds.has(v.id))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedViewerIds(new Set(viewers.map(v => v.id)));
                                  } else {
                                    setSelectedViewerIds(new Set());
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                            </th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">ID</th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">ユーザー名</th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">ポイント</th>
                            <th className="text-left py-3 px-4 text-gray-700 font-semibold">作成日</th>
                            <th className="text-right py-3 px-4 text-gray-700 font-semibold">操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewers.map((viewer) => (
                            <tr
                              key={viewer.id}
                              className={`border-b border-gray-200 hover:bg-gray-100/50 ${selectedViewerIds.has(viewer.id) ? 'bg-blue-50' : ''}`}
                            >
                              <td className="py-3 px-4">
                                <input
                                  type="checkbox"
                                  checked={selectedViewerIds.has(viewer.id)}
                                  onChange={() => {
                                    const newSelected = new Set(selectedViewerIds);
                                    if (newSelected.has(viewer.id)) {
                                      newSelected.delete(viewer.id);
                                    } else {
                                      newSelected.add(viewer.id);
                                    }
                                    setSelectedViewerIds(newSelected);
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                              </td>
                              <td className="py-3 px-4 text-left text-gray-700">{viewer.id}</td>
                              <td className="py-3 px-4 text-left text-gray-900 font-medium">{viewer.username}</td>
                              <td className="py-3 px-4 text-left text-yellow-400 font-semibold">
                                {viewer.points.toLocaleString()} pt
                              </td>
                              <td className="py-3 px-4 text-left text-gray-600 text-sm">
                                {new Date(viewer.created_at).toLocaleDateString('ja-JP')}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <button
                                  onClick={() => handleDeleteViewer(viewer.id, viewer.username)}
                                  className="text-red-400 hover:text-red-300 font-semibold"
                                >
                                  削除
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                      currentPage={viewerPage}
                      totalPages={viewerTotalPages}
                      totalItems={viewerTotal}
                      itemsPerPage={viewerLimit}
                      onPageChange={handleViewerPageChange}
                      onItemsPerPageChange={handleViewerLimitChange}
                    />
                  </>
                )}

                {/* Bulk Notification Modal */}
                {showBulkNotificationModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 shadow-xl max-w-md w-full mx-4">
                      <h3 className="font-bold text-lg text-gray-900 mb-4">
                        一括通知送信（{selectedViewerIds.size}件）
                      </h3>
                      <div className="space-y-4 mb-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            タイトル <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={bulkNotificationTitle}
                            onChange={(e) => setBulkNotificationTitle(e.target.value)}
                            required
                            className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="通知のタイトル"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            本文 <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={bulkNotificationMessage}
                            onChange={(e) => setBulkNotificationMessage(e.target.value)}
                            required
                            rows={4}
                            className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="通知の本文"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            リンクURL（任意）
                          </label>
                          <input
                            type="url"
                            value={bulkNotificationLinkUrl}
                            onChange={(e) => setBulkNotificationLinkUrl(e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="https://example.com"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setShowBulkNotificationModal(false);
                            setBulkNotificationTitle('');
                            setBulkNotificationMessage('');
                            setBulkNotificationLinkUrl('');
                          }}
                          className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 transition-colors"
                          disabled={isSendingBulkNotification}
                        >
                          キャンセル
                        </button>
                        <button
                          onClick={handleSendBulkNotification}
                          className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 transition-colors disabled:opacity-50"
                          disabled={isSendingBulkNotification || !bulkNotificationTitle.trim() || !bulkNotificationMessage.trim()}
                        >
                          {isSendingBulkNotification ? '送信中...' : '送信'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Affiliations Tab (Admin only) */}
        {activeTab === 'affiliations' && isAdmin && (
          <div className="bg-white shadow-md p-6 mb-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">グループ管理 ({affiliations.length})</h2>
              <button
                onClick={() => {
                  setShowCreateAffiliationForm(true);
                  setEditingAffiliation(null);
                  setNewAffiliationName('');
                  setNewAffiliationDescription('');
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 transition-colors shadow-md"
              >
                + グループ作成
              </button>
            </div>

            {/* Create Affiliation Modal */}
            {showCreateAffiliationForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white p-6 shadow-xl max-w-md w-full mx-4">
                  <h3 className="font-bold text-lg text-gray-900 mb-4">新規グループ作成</h3>
                  <form onSubmit={handleCreateAffiliation}>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">グループ名 <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={newAffiliationName}
                          onChange={(e) => setNewAffiliationName(e.target.value)}
                          required
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="グループ名を入力"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">説明（任意）</label>
                        <input
                          type="text"
                          value={newAffiliationDescription}
                          onChange={(e) => setNewAffiliationDescription(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="グループの説明を入力"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateAffiliationForm(false);
                          setNewAffiliationName('');
                          setNewAffiliationDescription('');
                        }}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 transition-colors"
                      >
                        キャンセル
                      </button>
                      <button
                        type="submit"
                        className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-6 transition-colors"
                      >
                        作成
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Affiliation Modal */}
            {editingAffiliation && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white p-6 shadow-xl max-w-md w-full mx-4">
                  <h3 className="font-bold text-lg text-gray-900 mb-4">グループ編集: {editingAffiliation.name}</h3>
                  <form onSubmit={handleUpdateAffiliation}>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">グループ名</label>
                        <input
                          type="text"
                          value={newAffiliationName}
                          onChange={(e) => setNewAffiliationName(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="グループ名を入力"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">説明</label>
                        <input
                          type="text"
                          value={newAffiliationDescription}
                          onChange={(e) => setNewAffiliationDescription(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="グループの説明を入力"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={resetAffiliationForm}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 transition-colors"
                      >
                        キャンセル
                      </button>
                      <button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 transition-colors"
                      >
                        更新
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Affiliations List */}
            {isLoadingAffiliations ? (
              <div className="text-center text-gray-600 py-8">グループを読み込み中...</div>
            ) : affiliations.length === 0 ? (
              <div className="text-center text-gray-600 py-8">グループが登録されていません</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">ID</th>
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">グループ名</th>
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">説明</th>
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">ユーザー数</th>
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">作成日</th>
                      <th className="text-right py-3 px-4 text-gray-700 font-semibold">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliations.map((affiliation) => (
                      <tr key={affiliation.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 text-left text-gray-700">{affiliation.id}</td>
                        <td className="py-3 px-4 text-left">
                          <span className="font-medium text-gray-900">{affiliation.name}</span>
                          {affiliation.name === 'default' && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">デフォルト</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-left text-gray-600 text-sm">
                          {affiliation.description || '-'}
                        </td>
                        <td className="py-3 px-4 text-left">
                          <span className="px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded">
                            {affiliation.user_count}人
                          </span>
                        </td>
                        <td className="py-3 px-4 text-left text-gray-600 text-sm">
                          {new Date(affiliation.created_at).toLocaleDateString('ja-JP')}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => startEditAffiliation(affiliation)}
                              className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                            >
                              編集
                            </button>
                            {affiliation.name !== 'default' && (
                              <button
                                onClick={() => handleDeleteAffiliation(affiliation.id, affiliation.name)}
                                className="text-red-600 hover:text-red-800 font-semibold text-sm"
                                disabled={affiliation.user_count > 0}
                                title={affiliation.user_count > 0 ? 'ユーザーが所属しているため削除できません' : undefined}
                              >
                                削除
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Schedules Tab */}
        {activeTab === 'schedules' && (
          <div className="bg-white shadow-md p-6 mb-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">配信スケジュール ({schedules.length})</h2>
              <button
                onClick={() => {
                  setShowCreateScheduleForm(!showCreateScheduleForm);
                  setEditingSchedule(null);
                  resetScheduleForm();
                }}
                className="bg-info hover:bg-info-600 text-white font-semibold py-2 px-4 transition-colors shadow-md"
              >
                {showCreateScheduleForm ? 'キャンセル' : '+ スケジュール作成'}
              </button>
            </div>

            {/* Create Schedule Form */}
            {showCreateScheduleForm && (
              <form onSubmit={handleCreateSchedule} className="mb-6 p-4 bg-gray-50 border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-3">新規スケジュール作成</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">配信者</label>
                    <select
                      value={newScheduleStreamerId}
                      onChange={(e) => setNewScheduleStreamerId(Number(e.target.value))}
                      required
                      className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                    >
                      <option value={0}>選択してください</option>
                      {streamers.map((s) => (
                        <option key={s.id} value={s.id}>{s.username}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">開始日時</label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={getDatePart(newScheduleStart)}
                        onChange={(e) => {
                          const currentTime = getTimePart(newScheduleStart) || '12:00';
                          const newDateTime = combineDateAndTime(e.target.value, currentTime);
                          setNewScheduleStart(newDateTime);
                          // 終了日時を+2時間に自動設定
                          if (newDateTime) {
                            const startDate = new Date(newDateTime);
                            startDate.setHours(startDate.getHours() + 2);
                            // ローカルタイムゾーンで日付を取得
                            const endDate = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
                            const endTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
                            setNewScheduleEnd(combineDateAndTime(endDate, endTime));
                          }
                        }}
                        required
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                      />
                      <select
                        value={getTimePart(newScheduleStart) || '12:00'}
                        onChange={(e) => {
                          const currentDate = getDatePart(newScheduleStart);
                          const newDateTime = combineDateAndTime(currentDate, e.target.value);
                          setNewScheduleStart(newDateTime);
                          // 終了日時を+2時間に自動設定
                          if (currentDate && e.target.value) {
                            const startDate = new Date(newDateTime);
                            startDate.setHours(startDate.getHours() + 2);
                            // ローカルタイムゾーンで日付を取得
                            const endDate = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
                            const endTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
                            setNewScheduleEnd(combineDateAndTime(endDate, endTime));
                          }
                        }}
                        required
                        className="w-24 px-2 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                      >
                        {timeOptions.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">終了日時</label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={getDatePart(newScheduleEnd)}
                        onChange={(e) => setNewScheduleEnd(combineDateAndTime(e.target.value, getTimePart(newScheduleEnd) || '14:00'))}
                        required
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                      />
                      <select
                        value={getTimePart(newScheduleEnd) || '14:00'}
                        onChange={(e) => setNewScheduleEnd(combineDateAndTime(getDatePart(newScheduleEnd), e.target.value))}
                        required
                        className="w-24 px-2 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                      >
                        {timeOptions.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 transition-colors"
                >
                  作成
                </button>
              </form>
            )}


            {/* Schedules List */}
            {isLoadingSchedules ? (
              <div className="text-center text-gray-600 py-8">スケジュールを読み込み中...</div>
            ) : schedules.length === 0 ? (
              <div className="text-center text-gray-600 py-8">スケジュールが登録されていません</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">配信者</th>
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">開始日時</th>
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">終了日時</th>
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">ステータス</th>
                      <th className="text-right py-3 px-4 text-gray-700 font-semibold">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((schedule) => (
                      <tr key={schedule.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 text-left">
                          <div className="flex items-center gap-2">
                            {schedule.streamer_profile_image_url && (
                              <img
                                src={resolveMediaUrl(schedule.streamer_profile_image_url)}
                                alt=""
                                className="w-8 h-8 object-cover"
                              />
                            )}
                            <div>
                              <div className="font-medium text-gray-900">
                                {schedule.streamer_display_name || schedule.streamer_username}
                              </div>
                              {schedule.streamer_display_name && (
                                <div className="text-xs text-gray-500">@{schedule.streamer_username}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-left text-gray-700 text-sm">{formatDateTime(schedule.scheduled_start)}</td>
                        <td className="py-3 px-4 text-left text-gray-700 text-sm">{formatDateTime(schedule.scheduled_end)}</td>
                        <td className="py-3 px-4 text-left">
                          <span className={`px-2 py-1 text-xs font-semibold ${getStatusBadge(schedule.status)}`}>
                            {getStatusLabel(schedule.status)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-2">
                            <a
                              href={ROUTES.WATCH_STREAMER(schedule.streamer_username || '')}
                              className="text-pink-600 hover:text-pink-800 font-semibold text-sm"
                            >
                              視聴
                            </a>
                            <button
                              onClick={() => startEditSchedule(schedule)}
                              className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                            >
                              編集
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.id, schedule.streamer_display_name || schedule.streamer_username || '')}
                              className="text-red-600 hover:text-red-800 font-semibold text-sm"
                            >
                              削除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="bg-white shadow-md p-6 mb-6 border border-gray-200 text-left">
            {/* Schedule Notice Prefix Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">おしらせ固定文言</h3>
              <p className="text-sm text-gray-600 mb-3">
                配信者プロフィールの「おしらせ」セクションに表示される固定文言です。<br />
                配信スケジュールの前に表示されます。
              </p>
              {isLoadingSettings ? (
                <div className="text-center text-gray-600 py-4">読み込み中...</div>
              ) : (
                <div className="space-y-4">
                  <textarea
                    value={scheduleNoticePrefix}
                    onChange={(e) => setScheduleNoticePrefix(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                    placeholder="いつもご視聴ありがとうございます！&#10;次回の配信予定をお知らせします。"
                  />
                  <button
                    onClick={handleSaveScheduleNoticePrefix}
                    disabled={isSavingSchedulePrefix}
                    className="bg-pink-500 hover:bg-pink-600 text-white font-semibold py-2 px-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingSchedulePrefix ? '保存中...' : '保存'}
                  </button>
                </div>
              )}
            </div>

          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="bg-white shadow-md p-6 mb-6 border border-gray-200 text-left">
            <h2 className="text-xl font-bold text-gray-900 mb-4 text-left">通知送信</h2>

            <form onSubmit={handleSendNotification} className="space-y-4 text-left">
              {/* 送信先とタイトルを同じ行に */}
              <div className="flex gap-4">
                <div className="w-40 shrink-0">
                  <label className="block text-sm font-medium text-gray-700 mb-2">送信先</label>
                  <select
                    value={notificationRecipientType}
                    onChange={(e) => setNotificationRecipientType(e.target.value as 'all' | 'individual')}
                    className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                  >
                    <option value="all">全視聴者</option>
                    <option value="individual">個別視聴者</option>
                  </select>
                </div>

                {notificationRecipientType === 'individual' && (
                  <div className="w-48 shrink-0">
                    <label className="block text-sm font-medium text-gray-700 mb-2">視聴者</label>
                    <select
                      value={notificationRecipientId || ''}
                      onChange={(e) => setNotificationRecipientId(e.target.value ? Number(e.target.value) : undefined)}
                      required
                      className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                    >
                      <option value="">選択してください</option>
                      {viewers.map((viewer) => (
                        <option key={viewer.id} value={viewer.id}>{viewer.username}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">タイトル</label>
                  <input
                    type="text"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                    placeholder="通知のタイトルを入力"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">メッセージ</label>
                <textarea
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  required
                  rows={4}
                  className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                  placeholder="通知のメッセージを入力"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">リンクURL（任意）</label>
                <input
                  type="url"
                  value={notificationLinkUrl}
                  onChange={(e) => setNotificationLinkUrl(e.target.value)}
                  autoComplete="off"
                  className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">画像（任意）</label>
                {/* リサイズに関する注意書き */}
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 text-sm text-blue-800">
                  <p className="font-medium mb-1">📐 画像サイズについて</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>アップロードされた画像は <strong>244×165ピクセル</strong> にリサイズされます</li>
                    <li>アスペクト比が異なる場合、中央を基準に余白部分がクロップ（切り取り）されます</li>
                    <li>極端に縦長・横長の画像は大幅にクロップされる場合があります</li>
                  </ul>
                </div>
                {!notificationImageUrl ? (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageFileChange}
                      disabled={isUploadingImage}
                      className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info file:mr-4 file:py-1 file:px-4 file:file:border-0 file:text-sm file:font-semibold file:bg-info file:text-white hover:file:bg-info-600 disabled:opacity-50"
                    />
                    {isUploadingImage && (
                      <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-info" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        アップロード中...
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-500">JPG, PNG, WebP, GIF（最大5MB）</p>
                  </div>
                ) : (
                  <div className="mt-2">
                    <div className="relative inline-block">
                      <img
                        src={resolveMediaUrl(notificationImageUrl)}
                        alt="プレビュー"
                        className="max-w-xs max-h-32 border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                        title="画像を削除"
                      >
                        ×
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {notificationImageFile?.name || '画像がアップロードされました（244x165）'}
                    </p>
                    {imageUploadWarning && (
                      <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1">
                        ⚠️ {imageUploadWarning}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isSendingNotification}
                className="bg-info hover:bg-info-600 text-white font-semibold py-2 px-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSendingNotification ? '送信中...' : '通知を送信'}
              </button>
            </form>
          </div>
        )}

        {showNotificationSuccessModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="notification-success-title"
              className="w-full max-w-sm bg-white p-6 text-center shadow-xl"
            >
              <h2 id="notification-success-title" className="mb-3 text-xl font-bold text-gray-900">
                通知を送信しました
              </h2>
              <p className="mb-6 text-gray-700">通知の送信が完了しました。</p>
              <button
                type="button"
                onClick={() => setShowNotificationSuccessModal(false)}
                className="bg-info px-6 py-2 font-semibold text-white transition-colors hover:bg-info-600"
              >
                閉じる
              </button>
            </div>
          </div>
        )}

        {/* Moderation Tab: monitoring, force-stop, image review, takedowns */}
        {activeTab === 'moderation' && (
          <div className="bg-white shadow-md border border-gray-200 text-left mb-6">
            <ModerationConsole isAdmin={isAdmin} />
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="bg-white shadow-md border border-gray-200 text-left mb-6">
            <ReportsManagementPanel />
          </div>
        )}

        {/* STT Settings Tab */}
        {activeTab === 'stt' && isAdmin && (
          <div className="bg-white shadow-md p-6 border border-gray-200 text-left mb-6">
            <SubtitleSettingsPanel />
          </div>
        )}

        {/* eKYC Admin Tab */}
        {activeTab === 'ekyc' && isAdmin && (
          <div className="bg-white shadow-md p-6 border border-gray-200 text-left mb-6">
            <EKYCAdminPanel />
          </div>
        )}

        {/* Banner Management Tab */}
        {activeTab === 'banners' && isAdmin && (
          <div className="bg-white shadow-md border border-gray-200 text-left mb-6">
            <BannerManagementPanel />
          </div>
        )}

        {activeTab === 'backups' && isAdmin && (
          <div className="bg-white shadow-md border border-gray-200 text-left mb-6">
            <BackupManagementPanel />
          </div>
        )}

      </div>

      {/* Edit Schedule Modal */}
      {showEditScheduleModal && editingSchedule && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && closeEditScheduleModal()}
        >
          <div className="bg-white shadow-xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="bg-blue-600 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                📅 スケジュール編集
              </h3>
            </div>
            <form onSubmit={handleUpdateSchedule} className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 font-medium">
                  配信者: {editingSchedule.streamer_display_name || editingSchedule.streamer_username}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">開始日時</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={getDatePart(newScheduleStart)}
                      onChange={(e) => handleEditStartTimeChange(combineDateAndTime(e.target.value, getTimePart(newScheduleStart) || '12:00'))}
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <select
                      value={getTimePart(newScheduleStart) || '12:00'}
                      onChange={(e) => handleEditStartTimeChange(combineDateAndTime(getDatePart(newScheduleStart), e.target.value))}
                      className="w-24 px-2 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {timeOptions.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">終了日時</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={getDatePart(newScheduleEnd)}
                      onChange={(e) => setNewScheduleEnd(combineDateAndTime(e.target.value, getTimePart(newScheduleEnd) || '14:00'))}
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <select
                      value={getTimePart(newScheduleEnd) || '14:00'}
                      onChange={(e) => setNewScheduleEnd(combineDateAndTime(getDatePart(newScheduleEnd), e.target.value))}
                      className="w-24 px-2 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {timeOptions.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                ※ 開始時間を変更すると、元の時間差分を維持して終了時間も自動調整されます
              </p>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
                <select
                  value={newScheduleStatus}
                  onChange={(e) => setNewScheduleStatus(e.target.value as ScheduleStatus)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="edit-schedule-status"
                >
                  <option value="scheduled">予定</option>
                  {/* 配信中・一時停止・中断は実際の配信状態でのみ設定されるため、表示のみ */}
                  {newScheduleStatus === 'live' && <option value="live" disabled>配信中</option>}
                  {newScheduleStatus === 'paused' && <option value="paused" disabled>一時停止</option>}
                  {newScheduleStatus === 'suspended' && <option value="suspended" disabled>中断</option>}
                  <option value="ended">終了</option>
                  <option value="cancelled">キャンセル</option>
                </select>
              </div>
              {editScheduleError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm" data-testid="edit-schedule-error">
                  {editScheduleError}
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeEditScheduleModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                >
                  更新
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Crop Confirmation Modal */}
      {showImageConfirmModal && pendingImageData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && handleCancelImage()}
        >
          <div className="bg-white shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-amber-500 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                ⚠️ 画像のクロップ確認
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {pendingImageData.warning}
              </p>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>クロップ後の画像プレビュー</strong>（実際に通知に表示される画像）
                </p>
                <div className="flex justify-center bg-gray-100 p-3 ">
                  <img
                    src={resolveMediaUrl(pendingImageData.url)}
                    alt="クロップ後のプレビュー"
                    className="border border-gray-300 shadow-sm w-[244px] h-[165px]"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">244×165ピクセル</p>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                この画像でよろしいですか？キャンセルして別の画像を選択することもできます。
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelImage}
                  className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleConfirmImage}
                  className="px-4 py-2 bg-info text-white font-semibold hover:bg-info-600 transition-colors"
                >
                  この画像を使用する
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
